import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
	Card,
	Descriptions,
	Divider,
	Pagination,
	Spin,
	Typography,
} from 'antd';
import baseuri from '../../methods/baseuri';
import { defaultImage } from '../../methods/utils/general-utils';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { getWalletSnapshotBalance } from '../../methods/redux/reducers/reports';
import remakeQueryString from '../../methods/utils/remake-query-string';
import BalanceItem from './balance-item';

const { Title, Text } = Typography;

const SnapshotDetails = () => {
	const { id } = useParams<{ id: string }>();
	const [data, setData] = useState({} as any);
	const [loading, setLoading] = useState(true);

	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();

	const { walletSnapshotBalance } = useAppSelector((store) => store.reports);

	const getSingleSnapshot = async () => {
		setLoading(true);
		try {
			const res: any = await baseuri.get('admin/wallet-snap-shot/single', {
				params: {
					snapShotId: id,
				},
			});

			if (res.status === 200) {
				setData(res.data.data);
			}
		} catch (error) {
			console.error('Error fetching snapshot details:', error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getSingleSnapshot();
	}, [id]);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(walletSnapshotBalance.paginator.perPage),
			});
		}

		dispatch(getWalletSnapshotBalance({ snapShotId: id, ...queryData }));
	}, [searchParams, id, dispatch, walletSnapshotBalance.paginator.perPage]);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	if (loading) {
		return <Spin size="large" />;
	}

	const { snapshot, balanceCount, totalBalance } = data;

	return (
		<Card title="Wallet Snapshot Details" style={{ margin: '20px' }}>
			<Title level={4}>{snapshot?.title}</Title>
			<Descriptions bordered column={1}>
				<Descriptions.Item label="Snapshot ID">
					<Text strong>{snapshot?._id}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Currency">
					<Text strong>
						<div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
							<img
								alt="icon"
								src={snapshot.currencyId?.iconUrl || defaultImage}
								height={20}
								width={20}
							/>
							{snapshot?.currencyId?.symbol}
						</div>
					</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Currency Type">
					<Text strong>{snapshot?.currencyId?.currencyForm}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Created At">
					<Text strong>{new Date(snapshot?.createdAt).toLocaleString()}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Updated At">
					<Text strong>{new Date(snapshot?.updatedAt).toLocaleString()}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Total Balance Count">
					<Text strong>{balanceCount.toLocaleString()}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Total Balance Amount">
					<Text strong>
						{totalBalance.toLocaleString()} {snapshot?.currencyId?.symbol}
					</Text>
				</Descriptions.Item>
			</Descriptions>

			<Title level={4} style={{ marginTop: '20px' }}>
				Currency Details
			</Title>
			<Descriptions bordered column={2}>
				<Descriptions.Item label="CoinGecko ID">
					<Text strong>{snapshot?.currencyId?.coinGeckoId}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="USD Value">
					<Text strong>${snapshot?.currencyId?.usdValue}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Decimal Precision">
					<Text strong>{snapshot?.currencyId?.decimalPrecision}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Minimum Withdrawal Amount">
					<Text strong>{snapshot?.currencyId?.minimumWithdrawalAmount}</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Staking APY">
					<Text strong>{snapshot?.currencyId?.stakingApyPercentage}%</Text>
				</Descriptions.Item>
				<Descriptions.Item label="Status">
					<Text
						style={{
							color: snapshot.currencyId?.status === 'active' ? 'green' : 'red',
						}}
					>
						{snapshot.currencyId?.status}
					</Text>
				</Descriptions.Item>
			</Descriptions>

			<br />

			<p>
				Total of{' '}
				<strong>{walletSnapshotBalance?.paginator.itemCount} items</strong>{' '}
				found
			</p>

			<Divider />

			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotBalance?.paginator.itemCount}
				current={walletSnapshotBalance?.paginator.currentPage}
				pageSize={walletSnapshotBalance?.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>

			<br />

			{walletSnapshotBalance?.itemsList.map((item: any) => (
				<BalanceItem key={item._id} item={item} />
			))}

			<br />

			<Divider />
			<Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotBalance?.paginator.itemCount}
				current={walletSnapshotBalance?.paginator.currentPage}
				pageSize={walletSnapshotBalance?.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/>
		</Card>
	);
};

export default SnapshotDetails;
